import React from 'react'
import { useNavigate } from 'react-router-dom'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import { usePayrollBundlePageContext } from 'modules/payroll_bundle/components/PayrollBundle'
import getUserByItem from 'modules/payroll_bundle/components/common/helper/getUserByItem'
import { LIST_PAYMENTS, UPDATE_DATA } from 'modules/payroll_bundle/lib/constants'
import { usePayrollPayments } from 'modules/payroll_bundle/lib/payrollPaymentsContext'
import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'
import { disablePaymentItem } from 'modules/payroll_bundle/pages/ResultsAndPaymentsPage/_common/disableRowItem'
import paymentItems from 'modules/payroll_bundle/store/collections/paymentItems'
import payments from 'modules/payroll_bundle/store/collections/payments'
import Payment from 'modules/payroll_bundle/store/models/Payment'
import type PaymentItem from 'modules/payroll_bundle/store/models/PaymentItem'

interface Props {
  selected: Set<string>
  builtPaymentItems: PaymentItem[]
}

const useGeneratePayment = ({ selected, builtPaymentItems }: Props) => {
  const [importing, setImporting] = React.useState(false)
  const { currentPeriod, currentPeriodStatus } = usePolicyPeriods()
  const { paymentDispatch, paymentConceptType } = usePayrollPayments()
  const canImport = selected.size
  const policyPeriodId = currentPeriod?.id

  const data = {
    data: {
      policy_period_id: policyPeriodId,
    },
  }

  const navigate = useNavigate()
  const { returnTo } = usePayrollBundlePageContext()

  const payload = () => {
    const payment_items = builtPaymentItems
      .filter((paymentItem) => {
        const user = getUserByItem(paymentItem)
        const disabled = disablePaymentItem(paymentItem, user)

        return !disabled && selected.has(paymentItem.id)
      })
      .map((paymentItem) => {
        return {
          employee_id: paymentItem.attributes.employee_id,
          amount_in_cents: paymentItem.attributes.amount,
          bank_account: paymentItem.attributes.bank_account,
        }
      })

    return {
      policy_period_id: policyPeriodId,
      payment_items,
      concept: paymentConceptType,
    }
  }

  const importData = async () => {
    if (importing) return

    setImporting(true)

    const payment = new Payment()

    payment.attributes.concept = paymentConceptType

    const { success } = await asyncAction(() => payment.save(payload()), {
      successMessage: i18n.t(
        'payroll_bundle.results_payments.payments.table.footer.import_successful'
      ),
    })

    if (success) {
      await Promise.all([payments.fetch(data), paymentItems.fetch(data)])

      paymentDispatch({
        type: UPDATE_DATA,
        payload: {
          selectedPayments: new Set([]),
          paymentList: [],
          generatedPayments: [],
          paymentStage: LIST_PAYMENTS,
        },
      })
      track(trackingEvents.payroll.generatePaymentFromSelectedResult, {
        period_status: currentPeriodStatus,
      })
    }

    setImporting(false)

    if (returnTo) {
      navigate(returnTo)
    }
  }

  return {
    isDisabled: !canImport || importing,
    onClick: importData,
    title: i18n.t('payroll_bundle.results_payments.payments.table.footer.generate_payment'),
  }
}

export default useGeneratePayment
